import type { Observable } from 'rxjs';
import { Subject, BehaviorSubject, of, merge } from 'rxjs';
import {
  buffer,
  concatMap,
  connect,
  debounceTime,
  filter,
  mergeMap,
  share,
  skipWhile,
  throttleTime
} from 'rxjs/operators';
import { useContext, useMemo, useCallback } from 'react';
import { shallowEqualObjects } from 'shallow-equal';

import { RealtimeContext } from 'app/common/RealtimeProvider';
import { useObservable } from 'app/hooks/useObservable';
import { HashiWsContext } from 'app/common/HashiWsProvider';
import { wsTopic } from 'app/utils/realTime';

import { concatScan } from './utils';
import type { CachedRessource, RessourceHookParams, RessourceParams, WSTopic } from './types';

const cache: CachedRessource[] = [];

function ressource<D, M>({
  socket,
  fetchInitialData,
  topics,
  handleMessage,
  localMessage,
  messagesBufferTime,
  bufferMessageType
}: RessourceParams<D, M>): Observable<D> {
  const subject = new BehaviorSubject<D | undefined>(undefined);
  return fetchInitialData.pipe(
    concatMap(data => {
      const observables: Observable<{ data: M[] }>[] = [
        // use `of` to execute the pipe with initial data
        // without that, `data` will be undefined until first websocket event
        of<{ data: any[] }>({ data: [{ type: 'init_ressource' }] }),
        localMessage
      ];
      if (topics) {
        topics.forEach(({ topic, topicParams, topicActions }) => {
          observables.push(wsTopic<{ data: M[] }>(socket, topic, topicParams, topicActions));
        });
      }

      if (messagesBufferTime !== undefined) {
        return merge(...observables).pipe(
          mergeMap(({ data: messages }) => of(...messages)),
          share({
            resetOnError: false,
            resetOnComplete: false,
            resetOnRefCountZero: false
          }),
          // See https://stackoverflow.com/questions/45591641/rxjs-making-a-buffer-emit-after-certain-event for reference
          connect(
            source$ => {
              // Buffer specified message types if bufferMessageType param is here.
              // Else, buffer everything
              const onlySpecifiedMessageTypes$ = source$.pipe(
                filter(event => {
                  if (
                    typeof event === 'object' &&
                    !!event &&
                    'type' in event &&
                    typeof event.type === 'string' &&
                    bufferMessageType !== undefined
                  ) {
                    return !bufferMessageType.includes(event.type);
                  }
                  return false;
                })
              );
              // Release buffer when type of message changes (seems to be not working tho, but I don't know how to do it differently)
              // const changedType$ = source$.pipe(distinctUntilKeyChanged('type' as keyof M));
              // Else, release buffer on debounced time
              const debounced$ = source$.pipe(debounceTime(messagesBufferTime));

              return source$.pipe(buffer(merge(onlySpecifiedMessageTypes$, debounced$)));
            },
            { connector: () => new Subject() }
          ),
          filter(messages => messages.length > 0),
          concatScan<M[], D>(handleMessage, data),
          throttleTime(100, undefined, { leading: false, trailing: true })
        );
      }

      return merge(...observables).pipe(
        mergeMap(({ data: messages }) => of(...messages)),
        concatScan<M, D>(handleMessage, data),
        throttleTime(100, undefined, { leading: false, trailing: true })
      );
    }),
    share({
      connector: () => subject as BehaviorSubject<D>,
      resetOnError: false,
      resetOnComplete: false,
      resetOnRefCountZero: true
    }),
    // We skip undefined data to avoid triggering a first undefined message
    // This is useful because the resource is used with the useObservable hook, which sets a loader up until it receives the first message
    // Without the skipWhile, a first undefined message would be received instantly and the loader would never be displayed
    skipWhile(data => data === undefined)
  );
}

const extractTopics = (topics: WSTopic[] | undefined): WSTopic['topic'][] =>
  topics ? topics.map(topic => topic.topic) : [];
const extractTopicsParams = (topics: WSTopic[] | undefined): WSTopic['topicParams'][] =>
  topics ? topics.map(topic => topic.topicParams).filter(param => !!param) : [];

/**
 * Try to get a ressource from cache or create it if it doesn't exist
 */
function getRessource<D, M>(params: RessourceParams<D, M>): Observable<D> {
  const { fetchInitialData, topics } = params;
  const cachedRessource = cache.find(
    c =>
      c.fetchInitial === fetchInitialData &&
      shallowEqualObjects(extractTopics(c.topics), extractTopics(topics)) &&
      shallowEqualObjects(extractTopicsParams(c.topics), extractTopicsParams(topics))
  );

  if (cachedRessource) {
    return cachedRessource.ressource;
  }

  const newRessource = ressource(params);
  cache.push({
    fetchInitial: fetchInitialData,
    topics,
    ressource: newRessource
  });

  return newRessource;
}

function removeInCache(ressourceToRemove: Observable<any>): void {
  const index = cache.findIndex(({ ressource }) => ressourceToRemove === ressource);
  if (index >= 0) {
    cache.splice(index, 1);
  }
}

function useRessource<D, M>({
  fetchInitialData,
  topics,
  handleMessage,
  messagesBufferTime,
  fromCompetitors = false,
  bufferMessageType,
  id,
  socket: paramSocket
}: RessourceHookParams<D, M>) {
  const HashiWsContextValue = useContext(HashiWsContext);
  const RealtimeContextValue = useContext(RealtimeContext);

  const { socket: regularSocket } = fromCompetitors ? HashiWsContextValue : RealtimeContextValue;
  const socket = paramSocket || regularSocket;

  const localMessage = useMemo(() => new Subject<{ data: M[] }>(), []);
  const pushMessage = useCallback((message: M) => {
    localMessage.next({ data: [message] });
  }, []);

  const params =
    messagesBufferTime !== undefined
      ? {
          socket: socket!,
          fetchInitialData,
          topics,
          handleMessage,
          localMessage,
          messagesBufferTime,
          bufferMessageType
        }
      : {
          socket: socket!,
          fetchInitialData,
          topics,
          handleMessage,
          localMessage
        };

  const instanceRessource = getRessource(params);
  const { status, data, error, stop, withLoader } = useObservable(instanceRessource, { id });

  // As we use a behavior subject, unsubscribe => subscribe doesn't restart observer
  // so we remove subject from cache to generate a new behavior subject after rerender fire by `stop`
  const restart = () => {
    removeInCache(instanceRessource);
    stop();
  };

  return { withLoader, status, data, error, restart, stop, pushMessage };
}

export { useRessource };
